import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { type TLEditorSnapshot } from "tldraw";

import { Logger } from "@moment/logging";

import { $documentLocalPath } from "../document-list";
import { env } from "../env";
import { fs } from "../filesystem";
import { paths } from "../paths";
import { canvasSnapshotKeyForDocsRoute } from "./canvasSnapshotKey";

const logger = new Logger("canvas-editor-state");

// ============================================================================
// Types
// ============================================================================

interface CanvasSnapshotContainer {
	snapshot: TLEditorSnapshot | undefined;
	params: CanvasSnapshotParams;
}

interface CanvasSnapshotParams {
	key: string;
	documentId: string;
	pageId: string;
}

// ============================================================================
// Canvas Snapshot State
// ============================================================================

/**
 * Private atom to hold the current canvas snapshot value and params
 * Starts as null, gets set when snapshots are loaded or dispatched
 *
 * @internal
 */
const $canvasSnapshotValue = atomFamily((key: string) => {
	const valueAtom = atom(null as CanvasSnapshotContainer | null);
	valueAtom.debugLabel = `canvasSnapshotValue::${key}`;
	return valueAtom;
});

/**
 * Public atom to get the current canvas snapshot
 * Returns the snapshot if loaded, undefined otherwise
 */
export const $pageCanvasSnapshot = atomFamily((key: string) => {
	const snapshotAtom = atom((get): TLEditorSnapshot | undefined => {
		const container = get($canvasSnapshotValue(key));
		return container?.snapshot;
	});
	snapshotAtom.debugLabel = `pageCanvasSnapshot::${key}`;
	return snapshotAtom;
});

/**
 * Tracks the last timestamp when canvas persistence was triggered
 * Used for debouncing
 */
const $canvasPersistenceTimestamp = atomFamily((key: string) => {
	const timestampAtom = atom(0);
	timestampAtom.debugLabel = `canvasPersistenceTimestamp::${key}`;
	return timestampAtom;
});

/**
 * Writable atom that triggers canvas persistence with debouncing
 * Only persists after 2 seconds of no updates
 */
const $triggerCanvasPersistence = atomFamily((key: string) =>
	atom(
		(get) => get($canvasPersistenceTimestamp(key)),
		(get, set, timestamp: number) => {
			set($canvasPersistenceTimestamp(key), timestamp);

			// Debounce: only persist after 2 seconds of no updates
			setTimeout(async () => {
				const current = get($canvasPersistenceTimestamp(key));
				if (current === timestamp) {
					// No updates in 2s, persist now
					try {
						await set($saveCanvasToDisk(key));
					} catch (error) {
						logger.error("Error persisting canvas", { key, error });
					}
				}
			}, 2000);
		}
	)
);

/**
 * Writable atom that saves the current canvas snapshot to disk
 */
const $saveCanvasToDisk = atomFamily((key: string) =>
	atom(null, async (get, set) => {
		if (!env.isDesktopApp) {
			logger.debug("Not saving canvas to disk: not in desktop app", { key });
			return;
		}

		const container = get($canvasSnapshotValue(key));
		if (!container?.snapshot) {
			logger.debug("Not saving canvas to disk: no snapshot", { key });
			return;
		}

		const { snapshot, params } = container;
		const { documentId, pageId } = params;

		const documentPath = await get($documentLocalPath(documentId));
		if (!documentPath) {
			logger.debug("Not saving canvas to disk: no document path", { key, documentId });
			return;
		}

		const pathsApi = paths();
		const pageDirPath = await get(pathsApi.getPageDirPath({ documentId, pageId }));

		const fsApi = fs();

		// Ensure page directory exists
		await set(fsApi.mkdir, {
			dirPath: pageDirPath,
			options: { recursive: true },
		});

		// Write canvas snapshot as JSON
		const canvasFilePath = await get(pathsApi.getPageCanvasFilePath({ documentId, pageId }));

		await set(fsApi.writeTextFile, {
			filePath: canvasFilePath,
			content: JSON.stringify(snapshot, null, 2),
			documentId,
		});

		logger.debug("Saved canvas to disk", { key, documentId, pageId, canvasFilePath });
	})
);

/**
 * Writable atom for setting canvas snapshots
 * Automatically triggers debounced persistence
 */
export const $setCanvasSnapshot = atomFamily((key: string) =>
	atom(null, (get, set, snapshot: TLEditorSnapshot) => {
		const container = get($canvasSnapshotValue(key));
		if (!container) {
			logger.error("Cannot set canvas snapshot: not initialized", { key });
			return;
		}

		set($canvasSnapshotValue(key), { ...container, snapshot });
		set($triggerCanvasPersistence(key), Date.now());
	})
);

// ============================================================================
// Canvas Snapshot Loading (for document loader)
// ============================================================================

export interface LoadCanvasSnapshotFromDiskInput {
	key: string;
	documentId: string;
	pageId: string;
	documentPath: string;
}

const fsApi = fs();

/**
 * Write-only atom that loads a canvas snapshot from disk.
 * Called by the document loader effect to optimistically load canvas data.
 */
export const $loadCanvasSnapshotFromDisk = atom(
	null,
	async (get, set, input: LoadCanvasSnapshotFromDiskInput) => {
		const { key, documentId, pageId } = input;

		// Initialize the container with params even if we don't load a snapshot
		const params: CanvasSnapshotParams = { key, documentId, pageId };

		try {
			if (!env.isDesktopApp) {
				logger.debug("Not loading canvas from disk: not in desktop app", { key });
				set($canvasSnapshotValue(key), { snapshot: undefined, params });
				return;
			}

			const pathsApi = paths();
			const canvasFilePath = await get(
				pathsApi.getPageCanvasFilePath({ documentId, pageId })
			);
			const exists = await get(fsApi.exists({ filePath: canvasFilePath }));

			if (!exists) {
				logger.debug("Canvas file does not exist on disk", { key, canvasFilePath });
				set($canvasSnapshotValue(key), { snapshot: undefined, params });
				return;
			}

			const raw = await get(fsApi.readTextFile({ filePath: canvasFilePath }));

			if (raw.trim() === "") {
				logger.debug("Canvas file is empty", { key, canvasFilePath });
				set($canvasSnapshotValue(key), { snapshot: undefined, params });
				return;
			}

			const snapshot = JSON.parse(raw) as TLEditorSnapshot;

			logger.info("Loaded canvas snapshot from disk", { key, canvasFilePath });

			set($canvasSnapshotValue(key), { snapshot, params });
		} catch (error) {
			logger.error("Error loading canvas snapshot from disk", { key, error });
			set($canvasSnapshotValue(key), { snapshot: undefined, params });
		}
	}
);
$loadCanvasSnapshotFromDisk.debugLabel = "loadCanvasSnapshotFromDisk";

// ============================================================================
// Canvas Snapshot Initialization for Collab
// ============================================================================

export interface InitializeCanvasSnapshotForCollabInput {
	key: string;
	documentId: string;
	pageId: string;
}

/**
 * Write-only atom that initializes the canvas snapshot container for collab documents.
 * For collab, tldraw handles its own sync, so we just initialize the container with params
 * and set the snapshot to undefined.
 */
export const $initializeCanvasSnapshotForCollab = atom(
	null,
	(_get, set, input: InitializeCanvasSnapshotForCollabInput) => {
		const { key, documentId, pageId } = input;

		const params: CanvasSnapshotParams = { key, documentId, pageId };

		logger.info("Initializing canvas snapshot container for collab", {
			key,
			documentId,
			pageId,
		});

		set($canvasSnapshotValue(key), { snapshot: undefined, params });
	}
);
$initializeCanvasSnapshotForCollab.debugLabel = "initializeCanvasSnapshotForCollab";

// ============================================================================
// Canvas Snapshot Unloading
// ============================================================================

/**
 * Unloads a canvas snapshot from `key`, freeing all resources associated with it.
 * Flushes any pending saves before unloading to prevent data loss.
 */
export const $unloadCanvasSnapshot = atom(null, async (get, set, key: string) => {
	const container = get($canvasSnapshotValue(key));
	if (container?.snapshot) {
		logger.info("Flushing canvas snapshot before unload", { key });
		try {
			await set($saveCanvasToDisk(key));
		} catch (error) {
			logger.error("Error flushing canvas before unload", { key, error });
		}
	}

	// Guard against race condition: if another load happened during the async flush,
	// the container will have been replaced. Don't clobber the new state.
	const currentContainer = get($canvasSnapshotValue(key));
	if (currentContainer !== null && currentContainer !== container) {
		logger.info("Canvas was reloaded during unload, skipping cleanup", { key });
		return;
	}

	set($canvasSnapshotValue(key), null);
	$canvasSnapshotValue.remove(key);
	$pageCanvasSnapshot.remove(key);
	$canvasPersistenceTimestamp.remove(key);
	$triggerCanvasPersistence.remove(key);
	$saveCanvasToDisk.remove(key);
	$setCanvasSnapshot.remove(key);
	logger.info("Unloaded canvas snapshot", { key });
});
$unloadCanvasSnapshot.debugLabel = "unloadCanvasSnapshot";

// Re-export the key function for convenience
export { canvasSnapshotKeyForDocsRoute };
